import React, { Fragment, useContext, useMemo } from 'react';
import { ascending, bin, descending, deviation, mean, merge } from 'd3-array';
import { timeDay, timeMinute, timeSecond } from 'd3-time';

import GlucoseStats from './GlucoseStats';
import PresentationContext from '../PresentationContext';
import TargetContext from './TargetContext';
import TiR from './TiR';
import { mgdL, internalUnits, consts } from '../utils/consts';
import { glucoseMolarMass, glucoseAccessor } from '../utils/dataUtils';
import { parseDate } from '../utils/formats';

const debug = process.env.NODE_ENV !== 'production' && false;
/* Calculate the active time for CGM.
 * Divide each day to N minute chunks.
 * Inspect the data: is there at least one reading within those N minutes?
 * 
 * Abbott Freestyle Libre reader device only stores data points every 15 minutes
 * for times more than an hour apart from the manual scan.
 * 
 * Devices using bluetooth adapters (Miaomiao, Nightrider, Bubble, etc..) may
 * report data in varying intervals, not necessarily strictly every 5 minutes.
*/
function getActiveTime(data) {
  const days = Object.keys(data).sort();
  const slotSizeInMinutes = 15;
  const slotSizeInSeconds = (slotSizeInMinutes * 60);
  const timeSlots = days.reduce((arr, d) => {
    const date = parseDate(d);
    return [ ...arr, ...timeMinute.range(date, timeDay.offset(date, 1), slotSizeInMinutes)];
  }, []);
  const readings = days.reduce((arr, d) => {
    return [ ...arr, ...(data[d] || []).map(entry => new Date(entry.time)) ];
  }, []).sort(ascending);

  let readingsIndex = 0;
  let available = 0;
  let missed = 0;
  let extra = 0;

  slotsLoop:
  for (let i = 0; i < timeSlots.length; i += 1) {
    const slot = timeSlots[i];
    if (readingsIndex >= readings.length) {
      // This and all the following readings will be missed
      missed += timeSlots.length - i;
      break;
    }
    let readingOffset = timeSecond.count(slot, readings[readingsIndex]);
    while (readingOffset < 0) {
      // the matching reading is more than -2.5 minutes older than this slot
      if (debug) {
        console.log('Extra reading for time slot preceding', i, slot, 'Reading', readingsIndex,
          readings[readingsIndex]);
      }
      extra += 1;
      readingsIndex += 1;
      if (readingsIndex >= (readings.length)) {
        // No more readings
        // The next iteration will finalize the calculations and exit the outer loop
        continue slotsLoop;
      }
      readingOffset = timeSecond.count(slot, readings[readingsIndex]);
    }
    if (readingOffset > slotSizeInSeconds) { // further than the next time slot
      // Calculate how much we can advance i, and add missed readings
      // Note that there may be filtered days!
      const endOfSlotDay = timeDay.ceil(slot);
      const endOfDayOffset = timeSecond.count(slot, endOfSlotDay);

      const missedReadings = Math.floor(Math.min(readingOffset, endOfDayOffset) / slotSizeInSeconds);
      missed += missedReadings;
      if (debug) {
        const previousReading = readingsIndex > 0 ? readings[readingsIndex - 1] : 'none';
        const nextReading = readingsIndex < (readings.length - 2)
        ? readings[readingsIndex + 1]
        : 'none';
        console.log(`${missedReadings} missed reading${missedReadings > 1 ? 's' : ''} for time slot`,
          i, slot, 'Reading', readingsIndex, readings[readingsIndex],
          'Previous reading', previousReading, 'Next reading', nextReading);
      }
      if (missedReadings > 1) {
        i += missedReadings - 1;
      }
      // We're not advancing the readingsIndex, rather leave it to next iteration
    } else {
      if (debug) {
        console.log('Recorded reading for time slot ', i, slot, 'Reading', readingsIndex,
          readings[readingsIndex]);
      }
      available += 1;
      readingsIndex += 1;
    }
  };
  // TODO: Perform some sanity checks?
  const slots = timeSlots.length;
  const calculated = percentFormat(available / slots);
  const summary = `${
    available
  } out of ${
    slots
  } readings present, ${
    missed
  } missed slots, ${
    extra
  } extra readings`;
  return {
    calculated,
    summary,
  }
}

function percentFormat(d) {
  if (isNaN(d)) {
    return '?';
  }
  return `${(d * 100).toFixed(1)}%`;
}

function getGlucoseStats(data) {
  const days = Object.keys(data);
  const values = days.reduce((arr, d) => {
    return [ ...arr, ...(data[d] || []).map(glucoseAccessor) ];
  }, []).sort(ascending);
  const bgMean = mean(values);
  const bgDeviation = deviation(values);
  const meanMgdl = (internalUnits === mgdL) ? bgMean : (bgMean * glucoseMolarMass);
  return {
    mean: bgMean,
    deviation: bgDeviation,
    cv: bgDeviation / bgMean,
    gmi: (meanMgdl + 77.3) / 3560,
  };
}

const defaultTicks = consts[internalUnits].ticks;
const tickNameLookup = Object.keys(defaultTicks).reduce((o, k) => {
  o[defaultTicks[k]] = k;
  return o;
}, {});
const binNames = Object.values(defaultTicks).sort(descending).map(t => tickNameLookup[t]);
binNames.shift();

const emptyBins = {
  hyper: {
    value: 0,
    y: 0,
    time: 0,
  },
  high: {
    value: 0,
    y: 5,
    time: 0,
  },
  low: {
    value: 0,
    y: 35,
    time: 0,
  },
  hypo: {
    value: 0,
    y: 99,
    time: 0,
  },
  min: {
    value: 0,
    y: 100,
    time: 0,
  },
};

export function getRanges(data, binThresholds) {
  const merged = merge(Object.values(data));
  if (!merged.length) {
    return emptyBins;
  }
  const rangeBins = bin()
    .domain([binThresholds[0], binThresholds[binThresholds.length - 1]])
    .value(glucoseAccessor)
    .thresholds(binThresholds);
  const bins = rangeBins(merged).reverse();
  bins.shift(); // the max bin is always empty
  let y = 0;
  const namedBins = bins.reduce((o, b, i) => {
    const value = merged.length ? b.length / merged.length * 100 : 0;
    o[binNames[i]] = {
      value,
      y,
      time: (24 * 60 * value / 100).toFixed(0),
    };
    // This would be good for texts, but not for the boxes!
    // We need to work with overlapping text better anyway...
    // y += value ? value : 4.5;
    y += value;
    return o;
  }, {});
  return {
    ...emptyBins,
    ...namedBins,
  };
}

function GlucoseStatsRow(props) {
  const { sgvData, t, ...rest } = props;
  const {
    durationFormat,    
    formatDateRange,
    formatGlucose,
    formatGlucoseValue
  } = useContext(PresentationContext);
  const { targets } = useContext(TargetContext);

  const days = Object.keys(sgvData).sort();
  const lastDay = days[days.length - 1];

  const activeTime = getActiveTime(sgvData);

  const stats = getGlucoseStats(sgvData);

  const ticks = useMemo(() => ({
    ...defaultTicks,
    high: targets.top,
    low: targets.bottom,
  }), [targets]);

  const binThresholds = useMemo(() => {
    const thresholds = Object.values(ticks).sort(ascending);
    // Replace max value to include all possible values
    thresholds[thresholds.length - 1] = internalUnits === 'mmol/l' ? 50 : 1000;
    return thresholds;
  }, [ticks]);

  const ranges = getRanges(sgvData, binThresholds);

  const rangeData = useMemo(() => ({
    hyper: (
      <Fragment>
        {t('glucoseStatsRow.tir.hyper')} <small>(&gt;{
        formatGlucose({value: 250, unit: mgdL})
        })</small>
      </Fragment>
    ),
    high: t('glucoseStatsRow.tir.high'),
    low: (
      <Fragment>
        {t('glucoseStatsRow.tir.target')} <small>({
        formatGlucoseValue({value: targets.bottom, unit: mgdL})
        }-{
          formatGlucose({value: targets.top, unit: mgdL})
        })</small>
      </Fragment>
    ),
    hypo: t('glucoseStatsRow.tir.low'),
    min: (
      <Fragment>
        {t('glucoseStatsRow.tir.hypo')} <small>(&lt;{
        formatGlucose({value: 54, unit: mgdL})
        })</small>
      </Fragment>
    ),
  }), [formatGlucose, formatGlucoseValue, t, targets]);
  
  const title = `${
    formatDateRange(days)
  }, ${
    t('glucoseStatsRow.stats.timeRange', {count: days.length})    
  }\n${
    t('glucoseStatsRow.stats.cgmActiveTime', { percentage: activeTime.calculated })
  }\n${
    t('glucoseStatsRow.stats.summary.glucoseAverage')
  } ${
    formatGlucose({ value: stats.mean, units: internalUnits })
  }\n${
    t('glucoseStatsRow.stats.summary.gmi')    
  } ${
    percentFormat(stats.gmi)
  }\n${
    t('glucoseStatsRow.stats.summary.glucoseVariability')
  } (CV) ${
    percentFormat(stats.cv)
  }\n${
    t('glucoseStatsRow.tir.hyper')
  } ${
    t('glucoseStatsRow.tir.glucose')
  } (>${
    formatGlucoseValue({value: 250, unit: mgdL})
  }): ${
    ranges.hyper.value.toFixed(1)
  }% ${
    t('glucoseStatsRow.tir.ofTime')
  } (${
    durationFormat(ranges.hyper.time)
  })\n${
    t('glucoseStatsRow.tir.high')
  } ${
    t('glucoseStatsRow.tir.glucose')
  } (${
    formatGlucoseValue({value: targets.top, unit: internalUnits})
  }-${
    formatGlucose({value: 250, unit: mgdL})
  }): ${
    ranges.high.value.toFixed(1)
  }% ${
    t('glucoseStatsRow.tir.ofTime')
  } (${
    durationFormat(ranges.high.time)
  })\n${
    t('glucoseStatsRow.tir.inTarget')
  } (${
    formatGlucoseValue({value: targets.bottom, unit: internalUnits})
  }-${
    formatGlucose({value: targets.top, unit: internalUnits})
  }): ${
    ranges.low.value.toFixed(1)
  }% ${
    t('glucoseStatsRow.tir.ofTime')
  } (${
    durationFormat(ranges.low.time)
  })\n${
    t('glucoseStatsRow.tir.low')
  } ${
    t('glucoseStatsRow.tir.glucose')
  } (${
    formatGlucoseValue({value: 54, unit: mgdL})
  }-${
    formatGlucose({value: targets.bottom, unit: internalUnits})
  }): ${
    ranges.hypo.value.toFixed(1)
  }% ${
    t('glucoseStatsRow.tir.ofTime')
  } (${
    durationFormat(ranges.hypo.time)
  })\n${
    t('glucoseStatsRow.tir.hypo')
  } ${
    t('glucoseStatsRow.tir.glucose')
  } (<${
    formatGlucose({value: 54, unit: mgdL})
  }): ${
    ranges.min.value.toFixed(1)
  }% ${
    t('glucoseStatsRow.tir.ofTime')
  } (${
    durationFormat(ranges.min.time)
  })\n`;

/*
Erittäin korkea glukoosi (>13.9 mmol/l) 2.7% ajasta (39 min)
Korkea glukoosi (10.1-13,9) 20.1% ajasta (4 t 49 min)
Glukoosi tavoitealueella (3.9-10.0 mmol/l) 74.5% ajasta (17 t 53 min)
Matala glukoosi (3,9-3,0) 2.2% ajasta (32 min)
Erittäin matala glukoosi (<3.0 mmol/l) 0.5% ajasta (7 min)
*/

  return (
    <Fragment>
      <GlucoseStats { ...rest} activeTime={activeTime} days={days} lastDay={lastDay} stats={stats} t={t} />
      <TiR {...rest} binNames={binNames} data={sgvData} rangeData={rangeData} ranges={ranges} t={t} title={title} />
    </Fragment>
  );
}

export default GlucoseStatsRow;
